import { HttpResponse } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { AccountGroup, AccountGroupService, ProjectionFilter } from '@galaxy/account-group';
import { GetPartnerModelRequest, PartnerModelApiService } from '@vendasta/partner';
import {
  A2PRegistrationApiService,
  A2PRegistrationInfoInterface,
  A2PRegistrationStatusInterface,
  CreateA2PRegistrationInfoRequestInterface,
  GetA2PRegistrationInfoRequestInterface,
  GetA2PRegistrationInfoResponseInterface,
  GetLatestA2PRegistrationStatusRequestInterface,
  OwnerType,
} from '@vendasta/smsv2';
import { UpsertA2PRegistrationInfoRequestInterface } from '@vendasta/smsv2/lib/_internal/interfaces/api.interface';
import { GetLatestA2PRegistrationStatusResponse } from '@vendasta/smsv2/lib/_internal/objects';
import {
  BehaviorSubject,
  catchError,
  combineLatest,
  distinctUntilChanged,
  firstValueFrom,
  map,
  Observable,
  of,
  shareReplay,
} from 'rxjs';
import { switchMap } from 'rxjs/operators';
import { SmsRegistrationDependencies } from '../../interface';
import { Region } from './sms-registration-form/types';
import { NORTH_AMERICA_COUNTRIES, RegistrationStage } from './a2p-registration.constants';
import { SmsInjectionToken } from '../../token';
import { getRegistrationStage } from './a2p-helper-functions';

export interface A2PRegistrationState {
  a2pRegistrationInfo: A2PRegistrationInfoInterface;
  a2pRegistrationStatus: A2PRegistrationStatusInterface;
  RegistrationStage: RegistrationStage;
}

export interface OwnerLocation {
  ownerId: string;
  ownerType: OwnerType;
  streetAddress?: string;
  streetAddressSecondary?: string;
  city?: string;
  state?: string;
  postalCode?: string;
  country?: string;
}

@Injectable({
  providedIn: 'root',
})
export class A2PRegistrationService {
  private readonly A2PRegistrationState$$: BehaviorSubject<A2PRegistrationState> =
    new BehaviorSubject<A2PRegistrationState>({
      a2pRegistrationInfo: {} as A2PRegistrationInfoInterface,
      a2pRegistrationStatus: {} as A2PRegistrationStatusInterface,
      RegistrationStage: RegistrationStage.NotSubmitted,
    });

  public readonly A2PRegistrationState$: Observable<A2PRegistrationState> = this.A2PRegistrationState$$.asObservable();

  public readonly registrationStage$: Observable<RegistrationStage> = this.A2PRegistrationState$$.asObservable().pipe(
    map((state) => state.RegistrationStage),
  );

  public readonly a2PRegistrationInfo$: Observable<A2PRegistrationInfoInterface> = this.A2PRegistrationState$$.pipe(
    map((state) => state.a2pRegistrationInfo),
  );

  public readonly a2pRegistrationStatus$: Observable<A2PRegistrationStatusInterface> = this.A2PRegistrationState$$.pipe(
    map((state) => state.a2pRegistrationStatus),
  );

  public readonly showExternalBanner$: Observable<boolean> = this.registrationStage$.pipe(
    map((stage) => stage === RegistrationStage.NotSubmitted || stage === RegistrationStage.DataError),
    shareReplay(1),
  );

  ownerId$: Observable<string>;
  public ownerType: OwnerType = this.config.ownerType;

  constructor(
    private a2pRegistrationApiService: A2PRegistrationApiService,
    private partnerService: PartnerModelApiService,
    private accountGroupService: AccountGroupService,
    @Inject(SmsInjectionToken) private readonly config: SmsRegistrationDependencies,
  ) {
    this.ownerId$ = this.config.ownerId$.pipe(distinctUntilChanged(), shareReplay(1));

    const existingA2PRegistrationInfo$ = this.ownerId$.pipe(
      switchMap((ownerId) => {
        const request = {
          ownerId: ownerId,
        } as GetA2PRegistrationInfoRequestInterface;
        return this.a2pRegistrationApiService.getA2pRegistrationInfo(request).pipe(
          catchError(() => {
            console.error('Error getting A2P registration info');
            return of({} as GetA2PRegistrationInfoResponseInterface);
          }),
        );
      }),
    );

    const existingA2PRegistrationStatus$ = combineLatest([this.config.ownerId$, this.config.subaccountID$]).pipe(
      switchMap(([ownerId, subaccountId]) => {
        if (!ownerId || !subaccountId) {
          return of({} as GetLatestA2PRegistrationStatusResponse);
        }
        const request = {
          ownerId: ownerId,
          subaccountId: subaccountId,
        } as GetLatestA2PRegistrationStatusRequestInterface;
        return this.a2pRegistrationApiService.getLatestA2pRegistrationStatus(request).pipe(
          catchError((e) => {
            console.error('getLatestA2pRegistrationStatus error:', e);
            return of({} as GetLatestA2PRegistrationStatusResponse);
          }),
        );
      }),
      map((response) => response.status),
      catchError(() => of({} as A2PRegistrationStatusInterface)),
    );

    combineLatest([existingA2PRegistrationInfo$, existingA2PRegistrationStatus$]).subscribe(
      ([a2pRegistrationInfo, a2pRegistrationStatus]) => {
        const a2pInfo = a2pRegistrationInfo?.a2pRegistrationInfo ?? ({} as A2PRegistrationInfoInterface);
        const isSubmitted = !!a2pInfo?.submitted && a2pInfo?.submitted > new Date(2023, 1, 1);
        this.A2PRegistrationState$$.next({
          a2pRegistrationInfo: a2pInfo,
          a2pRegistrationStatus: a2pRegistrationStatus,
          RegistrationStage: getRegistrationStage(a2pRegistrationStatus, isSubmitted),
        });
      },
    );
  }

  public async upsertA2pRegistration(request: UpsertA2PRegistrationInfoRequestInterface): Promise<HttpResponse<null>> {
    const ownerId = await firstValueFrom(this.ownerId$);
    request.ownerId = ownerId;
    request.ownerType = this.config.ownerType;
    return await firstValueFrom(
      this.a2pRegistrationApiService.upsertA2pRegistrationInfo(request).pipe(
        map((response) => {
          this.updateA2pRegistrationInfo(request);
          return response;
        }),
      ),
    );
  }

  private updateA2pRegistrationInfo(
    a2pRegistrationInfo: CreateA2PRegistrationInfoRequestInterface | UpsertA2PRegistrationInfoRequestInterface,
  ): void {
    const currentState = this.A2PRegistrationState$$.getValue();
    this.A2PRegistrationState$$.next({
      ...currentState,
      a2pRegistrationInfo: {
        ...currentState.a2pRegistrationInfo,
        ...a2pRegistrationInfo,
        submitted: a2pRegistrationInfo.submitted ? new Date() : currentState.a2pRegistrationInfo.submitted,
      },
      RegistrationStage: getRegistrationStage(currentState.a2pRegistrationStatus, true),
    });
  }

  getAccountGroupInfo$(): Observable<AccountGroup> {
    return this.ownerId$.pipe(
      switchMap((ownerId) => {
        return this.accountGroupService
          .get(ownerId, new ProjectionFilter({ napData: true }))
          .pipe(catchError(() => of({} as AccountGroup)));
      }),
    );
  }

  getOwnerLocation$(): Observable<OwnerLocation> {
    if (this.ownerType === OwnerType.OWNER_TYPE_ACCOUNT_GROUP) {
      return this.getAccountGroupInfo$().pipe(
        map((accountGroupInfo) => {
          return {
            ownerId: accountGroupInfo?.accountGroupId,
            ownerType: OwnerType.OWNER_TYPE_ACCOUNT_GROUP,
            streetAddress: accountGroupInfo?.napData?.address,
            streetAddressSecondary: accountGroupInfo?.napData?.address2,
            city: accountGroupInfo?.napData?.city,
            state: accountGroupInfo?.napData?.state,
            postalCode: accountGroupInfo?.napData?.zip,
            country: accountGroupInfo?.napData?.country,
          };
        }),
      );
    } else if (this.ownerType === OwnerType.OWNER_TYPE_PARTNER) {
      return this.ownerId$.pipe(
        switchMap((partnerId) => this.partnerService.getPartnerModel(new GetPartnerModelRequest({ partnerId }))),
        map((partnerResponse) => {
          const partnerModel = partnerResponse?.partner;
          return {
            ownerId: partnerModel?.partnerId,
            ownerType: OwnerType.OWNER_TYPE_PARTNER,
            streetAddress: partnerModel?.addressLineOne,
            streetAddressSecondary: partnerModel?.addressLineTwo,
            city: partnerModel?.city,
            state: partnerModel?.stateCode,
            postalCode: partnerModel?.postalCode,
            country: partnerModel?.countryCode,
          };
        }),
      );
    }
    return of({} as OwnerLocation);
  }

  getPrefillA2PFormInfo(): Observable<A2PRegistrationInfoInterface> {
    return this.a2PRegistrationInfo$.pipe(
      switchMap((a2PRegistrationInfo) => {
        //If there is submitted A2P form data from different app
        if (a2PRegistrationInfo && Object.keys(a2PRegistrationInfo).length > 0) {
          return of(a2PRegistrationInfo);
        }

        if (this.ownerType === OwnerType.OWNER_TYPE_ACCOUNT_GROUP) {
          return this.getAccountGroupInfo$().pipe(
            map((accountGroupInfo) => {
              return {
                business: {
                  regionOfOperation: NORTH_AMERICA_COUNTRIES.includes(accountGroupInfo?.napData?.country)
                    ? Region.usaAndCanada
                    : '',
                  streetAddress: accountGroupInfo?.napData?.address,
                  streetAddressSecondary: accountGroupInfo?.napData?.address2,
                  state: accountGroupInfo?.napData?.state,
                  city: accountGroupInfo?.napData?.city,
                  postalCode: accountGroupInfo?.napData?.zip,
                  websiteUrl: accountGroupInfo?.napData?.website,
                },
              } as A2PRegistrationInfoInterface;
            }),
          );
        }

        return of({} as A2PRegistrationInfoInterface);
      }),
    );
  }
}
